import type { ReactNode } from 'react'
import React from 'react'

import type { ProviderFeatureFlags } from '@fourthwall/components'
import { FeatureFlagsProvider } from '@fourthwall/components'
import { isProduction } from '@fourthwall/utils/dist/dev'

import { useAdminModeContext } from '../../../modules/App/providers/AdminModeProvider'
import { useCurrentShopQuery } from '../../../modules/Dashboard/hooks/useCurrentShopQuery'
// import { useUsersQuery } from '../../../queries/useUsersQuery'
import { useDashboardPermissions } from '../DashboardPermissions'
import { hasMembershipsMessagesEnabled } from './hasMembershipsMessagesEnabled'
import { hasMembershipsWlaEnabled } from './hasMembershipsWlaEnabled'
import type { Feature } from './types'
import { usePlatformFeatureFlags } from './usePlatformFeatureFlags'

type DashboardFeatureFlagsProviderProps = {
  children: ReactNode
}

export const DashboardFeatureFlagsProvider = ({ children }: DashboardFeatureFlagsProviderProps) => {
  const { hasRole } = useDashboardPermissions()
  const { currentShopQuery } = useCurrentShopQuery(undefined, { staleTime: Infinity })
  const { hasFeatureFlag } = usePlatformFeatureFlags()

  // const { usersQuery } = useUsersQuery(undefined, { staleTime: Infinity })

  // const shopMember =
  //   usersQuery.isSuccess && !!usersQuery.data.users.find(({ email }) => email === user.email)

  const adminMode = useAdminModeContext()

  const admin = {
    enabled: !!adminMode?.isActive,
    description: 'Admins 🔐',
    highlight: true,
  }

  const membershipsEnabled = currentShopQuery.isSuccess

  const featureFlags: ProviderFeatureFlags<Feature> = {
    account: true,
    accountSettingsSecurity:
      hasFeatureFlag('dashboard:account_settings:security') || !!adminMode?.isActive,
    analyticsTabs: hasFeatureFlag('analytics_tabs') || !!adminMode?.isActive,
    analyticsTopProductsProfit:
      hasFeatureFlag('analytics:top_products_profit') || !!adminMode?.isActive,
    analyticsTrackingPixels: true,
    analyticsV2: hasFeatureFlag('dashboard:analytics_v2') || !!adminMode?.isActive,
    analyticsV2Compare:
      hasFeatureFlag('dashboard:analytics_v2') && hasFeatureFlag('dashboard:analytics_v2:compare'),
    appearanceMode: hasFeatureFlag('appearance_mode'),
    appSpotify: membershipsEnabled && hasFeatureFlag('app_spotify'),
    appBookfunnel: hasFeatureFlag('app_bookfunnel') || !!adminMode?.isActive,
    appLinktree: !!adminMode?.isActive,
    appPackiyo: hasFeatureFlag('app_packiyo') || !!adminMode?.isActive,
    appPledge: hasFeatureFlag('app_pledge') || !!adminMode?.isActive,
    appMeta: hasFeatureFlag('app_meta') || !!adminMode?.isActive,
    buyXGetYPromotion: hasFeatureFlag('buy_x_get_y_promotion'),
    checkoutCustomGtm: hasFeatureFlag('checkout_custom_gtm'),
    createNewSite: hasFeatureFlag('create_new_site'),
    creatorChallenge: true,
    dashboardSaveVisit: hasFeatureFlag('dashboard:save_visit'),
    developersCustomChatbot: hasFeatureFlag('developers:custom_chatbot') || !!adminMode?.isActive,
    developersSectionSettings:
      hasFeatureFlag('developers:section_settings') || !!adminMode?.isActive,
    giftCards: hasFeatureFlag('dashboard:gift_cards'),
    homeOnboarding:
      currentShopQuery.isSuccess &&
      new Date(currentShopQuery.data.createdAt) > new Date('2022-09-08'),
    homeProducts: hasFeatureFlag('dashboard:home_products'),
    homeScheduler: true, // still disabled when !homeOnboarding
    hsCodes: {
      enabled: !!adminMode?.isActive || hasRole('ROLE_COSTS_WRITE'),
      description: 'Admins 🔐',
      highlight: true,
    },
    integrationMeta: hasFeatureFlag('integration_meta'),
    launchStrategy: hasFeatureFlag('launch_strategy'),
    memberships: membershipsEnabled,
    membershipsCustomEmoji: membershipsEnabled && hasFeatureFlag('memberships_custom_emoji'),
    membershipsGifting: membershipsEnabled && hasFeatureFlag('memberships_gifting'),
    membershipsLiveStream: membershipsEnabled && hasFeatureFlag('memberships_live_stream'),
    membershipsMessages:
      membershipsEnabled &&
      currentShopQuery.isSuccess &&
      hasMembershipsMessagesEnabled(currentShopQuery.data.id),
    membershipsPostHtml: membershipsEnabled && hasFeatureFlag('memberships_post_html'),
    membershipsPostTags: membershipsEnabled && hasFeatureFlag('memberships_post_tags'),
    membershipsPublicVideo: membershipsEnabled && hasFeatureFlag('memberships_public_video'),
    membershipsWla: membershipsEnabled && hasMembershipsWlaEnabled(currentShopQuery.data.id),
    messagesSendTo: !isProduction(),
    multicurrencyCustomTheme: hasFeatureFlag('multicurrency_custom_theme'),
    multicurrencyCustomThemeYen: hasFeatureFlag('multicurrency_custom_theme_yen'),
    multicurrencySettings: true,
    partner: hasRole('ROLE_AGENCY') || admin,
    payoutSetup: hasRole('ROLE_PAYOUT_SETUP'),
    productAdvancedOptions: {
      enabled: !!adminMode?.isActive || hasRole('ROLE_BESPOKE_READ'),
      description: 'Admins 🔐',
      highlight: true,
    },
    productCategorization: !isProduction(),
    productCombinedOffer: hasFeatureFlag('dashboard:products:create:combined'),
    productBundleOffer: hasFeatureFlag('dashboard:products:create:bundle'),
    productBundleGenerateImage: hasFeatureFlag('dashboard:products:bundle:generate_image'),
    productGiftCard: hasFeatureFlag('dashboard:products:create:giftCard'),
    profitCostsBreakdown: hasFeatureFlag('dashboard:profit_costs_breakdown'),
    promotionCodesFreeShippingLowestOnly: hasFeatureFlag(
      'promotion_codes_free_shipping_lowest_only',
    ),
    reportsFirstTimePurchases: hasFeatureFlag('reports:first_time_purchases'),
    reportsLTV: hasFeatureFlag('reports:ltv'),
    reportsUTM: hasFeatureFlag('reports:utm'),
    storeDesignThemeVersion: admin,
    streamElementsGifting: hasFeatureFlag('stream_elements_gifting'),
    subscriptions: hasFeatureFlag('subscriptions'),
    subscriptionsProductDesignerStorageLimits: !isProduction(),
    subscriptionsStorageLimits: hasFeatureFlag('subscriptions:storage_limits'),
    themeEditorDynamicPreview: hasFeatureFlag('theme_editor:dynamic_preview'),
    themeEditorExperimentalStyles: hasFeatureFlag('theme_editor:experimental_styles'),
    themeEditorFastPreview: hasFeatureFlag('theme_editor:fast_preview'),
    themeEditorGenerateTheme:
      hasFeatureFlag('theme_editor:generate_theme') && !!adminMode?.isActive,
    themeEditorMobileSession: hasFeatureFlag('theme_editor:mobile_session'),
    themeEditorSectionStyles: hasFeatureFlag('theme_editor:section_styles') || admin,
    themeEditorOnboarding: hasFeatureFlag('theme_editor:onboarding'),
    themeEditorSectionFonts: hasFeatureFlag('theme_editor:section_fonts'),
    timezone: !!adminMode?.isActive || hasFeatureFlag('dashboard:timezone'),
    thankYouCard: hasFeatureFlag('dashboard:thank_you_card'),
  }

  return <FeatureFlagsProvider featureFlags={featureFlags}>{children}</FeatureFlagsProvider>
}
